import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import { createIdempotencyKey, formatDate, ORDER_STATUS } from "../api/display";
import type { Category, Dish, Order } from "../api/types";
import { useAuth } from "../auth";
import { DishPhoto, EmptyState, Toast } from "../components/Shared";
import {
  enableWebPushNotifications,
  getWebPushState,
  PUSH_EVENT,
  type PushEventDetail,
  type WebPushState,
} from "../push";

type View = "menu" | "cart" | "orders";
const MEAL_TIMES = ["尽快", "19:00", "20:00"];

export function CustomerApp() {
  const { user, logout } = useAuth();
  const [view, setView] = useState<View>(() => (
    new URLSearchParams(window.location.search).get("view") === "orders" ? "orders" : "menu"
  ));
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryId, setCategoryId] = useState("all");
  const [selected, setSelected] = useState<Record<string, number>>({});
  const [orders, setOrders] = useState<Order[]>([]);
  const [mealTime, setMealTime] = useState("19:00");
  const [note, setNote] = useState("");
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState("");
  const [pushState, setPushState] = useState<WebPushState>(() => getWebPushState());
  const [enablingPush, setEnablingPush] = useState(false);
  const toastTimer = useRef<number | undefined>(undefined);

  function announce(message: string) {
    setToast(message);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 2200);
  }

  async function loadMenu(silent = false) {
    try {
      const [nextCategories, nextDishes] = await Promise.all([
        api.listCategories(),
        api.listDishes(),
      ]);
      const visible = nextDishes.filter((dish) => dish.status !== "deleted");
      const availableIds = new Set(visible.filter((dish) => dish.status === "available").map((dish) => dish.id));
      setCategories(nextCategories.filter((item) => item.enabled));
      setDishes(visible);
      setSelected((current) => Object.fromEntries(
        Object.entries(current).filter(([dishId]) => availableIds.has(dishId)),
      ));
    } catch (reason) {
      if (!silent) announce(reason instanceof Error ? reason.message : "菜单同步失败");
    } finally {
      setLoadingMenu(false);
    }
  }

  async function loadOrders(silent = false) {
    try {
      const result = await api.listOrders();
      setOrders(result.items);
    } catch (reason) {
      if (!silent) announce(reason instanceof Error ? reason.message : "订单同步失败");
    } finally {
      setLoadingOrders(false);
    }
  }

  useEffect(() => {
    void loadMenu();
    void loadOrders();
    const orderTimer = window.setInterval(() => void loadOrders(true), 8000);
    const menuTimer = window.setInterval(() => void loadMenu(true), 15000);
    return () => {
      window.clearInterval(orderTimer);
      window.clearInterval(menuTimer);
      window.clearTimeout(toastTimer.current);
    };
  }, []);

  useEffect(() => {
    const refreshPushState = () => setPushState(getWebPushState());
    document.addEventListener("visibilitychange", refreshPushState);
    return () => document.removeEventListener("visibilitychange", refreshPushState);
  }, []);

  useEffect(() => {
    const handlePush = (event: Event) => {
      const detail = (event as CustomEvent<PushEventDetail>).detail;
      void loadOrders(true);
      if (detail.opened) setView("orders");
    };
    window.addEventListener(PUSH_EVENT, handlePush);
    return () => window.removeEventListener(PUSH_EVENT, handlePush);
  }, []);

  const visibleDishes = useMemo(
    () => categoryId === "all" ? dishes : dishes.filter((dish) => dish.category_id === categoryId),
    [categoryId, dishes],
  );
  const selectedCount = Object.values(selected).reduce((sum, quantity) => sum + quantity, 0);

  function toggleDish(dish: Dish) {
    if (dish.status !== "available") return;
    setSelected((current) => {
      const next = { ...current };
      if (next[dish.id]) delete next[dish.id];
      else next[dish.id] = 1;
      return next;
    });
    announce(selected[dish.id] ? "已从点菜单移出" : "已加入点菜单 ♥");
  }

  function changeQuantity(dishId: string, delta: number) {
    setSelected((current) => {
      const next = { ...current };
      const quantity = (next[dishId] || 0) + delta;
      if (quantity <= 0) delete next[dishId];
      else next[dishId] = Math.min(9, quantity);
      return next;
    });
  }

  async function submitOrder() {
    if (!selectedCount || submitting) return;
    setSubmitting(true);
    try {
      const order = await api.createOrder({
        mealTime,
        note,
        items: Object.entries(selected).map(([dishId, quantity]) => ({ dishId, quantity })),
      }, createIdempotencyKey());
      setOrders((current) => [order, ...current.filter((item) => item.id !== order.id)]);
      setSelected({});
      setNote("");
      setView("orders");
      announce("点菜单已经送到他的手机 💌");
    } catch (reason) {
      announce(reason instanceof Error ? reason.message : "下单失败");
    } finally {
      setSubmitting(false);
    }
  }

  async function enablePush() {
    if (enablingPush) return;
    setEnablingPush(true);
    try {
      const next = await enableWebPushNotifications();
      setPushState(next);
      if (next === "enabled") announce("订单提醒已开启 ♥");
      else if (next === "denied") announce("需要在 iPhone 设置中允许通知");
    } catch (reason) {
      announce(reason instanceof Error ? reason.message : "提醒开启失败");
      setPushState(getWebPushState());
    } finally {
      setEnablingPush(false);
    }
  }

  const cartDishes = dishes.filter((dish) => selected[dish.id]);

  return (
    <main className="mobile-app customer-app">
      <div className="safe-top" />
      <header className="app-topbar">
        <div className="brand-mini"><span>♡</span><p><small>FOR {user?.display_name || "HER"}</small><strong>她的私房菜单</strong></p></div>
        <button type="button" className="avatar-button" onClick={() => void logout()} aria-label="退出登录">👧🏻</button>
      </header>

      {view === "menu" && (
        <div className="screen menu-screen">
          <section className="customer-hero">
            <img src="/anime-couple.webp" alt="情侣在温暖的厨房准备晚餐" />
            <div className="hero-overlay" />
            <div className="hero-copy"><span><i />营业中</span><h1>今晚吃什么？</h1><p>只为你开的小厨房</p></div>
          </section>
          {pushState === "install-required" && (
            <section className="pwa-prompt" aria-label="安装提示">
              <span>📲</span>
              <p><strong>安装到 iPhone</strong><small>Safari 分享 → 添加到主屏幕</small></p>
            </section>
          )}
          {pushState === "needs-permission" && (
            <button type="button" className="pwa-prompt push-prompt" disabled={enablingPush} onClick={() => void enablePush()}>
              <span>💌</span>
              <p><strong>{enablingPush ? "正在开启…" : "开启订单提醒"}</strong><small>做好后第一时间告诉你</small></p>
              <b>开启</b>
            </button>
          )}
          {pushState === "denied" && (
            <section className="pwa-prompt muted" aria-label="通知设置提示">
              <span>🔕</span>
              <p><strong>订单提醒已关闭</strong><small>可在 iPhone 设置中重新允许</small></p>
            </section>
          )}
          <nav className="category-row" aria-label="菜品分类">
            <button className={categoryId === "all" ? "active" : ""} onClick={() => setCategoryId("all")}>全部</button>
            {categories.map((category) => (
              <button key={category.id} className={categoryId === category.id ? "active" : ""} onClick={() => setCategoryId(category.id)}>{category.name}</button>
            ))}
          </nav>
          {loadingMenu ? <div className="card-skeletons"><i /><i /><i /><i /></div> : visibleDishes.length ? (
            <section className="dish-grid">
              {visibleDishes.map((dish) => {
                const picked = Boolean(selected[dish.id]);
                return (
                  <article className={`dish-card ${picked ? "picked" : ""} ${dish.status !== "available" ? "sold-out" : ""}`} key={dish.id}>
                    <DishPhoto dish={dish} />
                    <div className="card-shade" />
                    <div className="dish-copy"><strong>{dish.name}</strong><small>◷ {dish.estimated_minutes} min</small></div>
                    <button type="button" onClick={() => toggleDish(dish)} disabled={dish.status !== "available"} aria-label={`选择${dish.name}`}>{dish.status !== "available" ? "×" : picked ? "♥" : "+"}</button>
                    {dish.status !== "available" && <b className="sold-badge">今日售罄</b>}
                  </article>
                );
              })}
            </section>
          ) : <EmptyState icon="🍱" title="菜单还是空的" hint="等他上架第一道菜" />}
          {selectedCount > 0 && <button type="button" className="floating-cart" onClick={() => setView("cart")}><span>♥ {selectedCount} 道</span><strong>确认点菜 →</strong></button>}
        </div>
      )}

      {view === "cart" && (
        <div className="screen detail-screen">
          <header className="detail-header"><button onClick={() => setView("menu")}>←</button><h1>确认点菜</h1><span>♥</span></header>
          <section className="cart-list">
            {cartDishes.map((dish) => (
              <article key={dish.id}><DishPhoto dish={dish} /><strong>{dish.name}</strong><div className="stepper"><button onClick={() => changeQuantity(dish.id, -1)}>−</button><span>{selected[dish.id]}</span><button onClick={() => changeQuantity(dish.id, 1)}>＋</button></div></article>
            ))}
          </section>
          <section className="order-options">
            <h2>🕖 开饭时间</h2>
            <div className="meal-times">{MEAL_TIMES.map((item) => <button key={item} className={mealTime === item ? "active" : ""} onClick={() => setMealTime(item)}>{item}</button>)}</div>
            <label className="note-input"><span>💬</span><input maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} placeholder="口味备注，比如少辣一点" /></label>
          </section>
          <button type="button" className="primary-action" disabled={!selectedCount || submitting} onClick={() => void submitOrder()}>{submitting ? "正在送达…" : "💌 送到厨房"}</button>
        </div>
      )}

      {view === "orders" && (
        <div className="screen orders-screen">
          <header className="section-heading"><div><small>OUR ORDERS</small><h1>全部订单</h1></div><b>{orders.length}</b></header>
          {loadingOrders && !orders.length ? <div className="order-skeleton" /> : orders.length ? (
            <section className="order-feed">
              {orders.map((order) => {
                const status = ORDER_STATUS[order.status];
                return (
                  <article className={`order-card status-${order.status}`} key={order.id}>
                    <header><time>{formatDate(order.created_at)}</time><span>{status.icon} {status.label}</span></header>
                    <div className="order-items-text">{order.items.map((item) => `${item.dish_name_snapshot} ×${item.quantity}`).join(" · ")}</div>
                    <p className="status-hint">{status.hint}</p>
                    <footer><span>🕖 {order.meal_time}</span><strong>{order.items.reduce((sum, item) => sum + item.quantity, 0)} 份</strong></footer>
                    {order.note && <div className="order-note">💬 {order.note}</div>}
                  </article>
                );
              })}
            </section>
          ) : <EmptyState icon="♡" title="还没有订单" hint="去菜单点一道喜欢的菜吧" />}
        </div>
      )}

      {view !== "cart" && <nav className="bottom-nav"><button className={view === "menu" ? "active" : ""} onClick={() => setView("menu")}><span>🍱</span><small>菜单</small></button><button className={view === "orders" ? "active" : ""} onClick={() => setView("orders")}><span>💌</span><small>订单</small></button></nav>}
      <Toast message={toast} />
    </main>
  );
}
